import React, { ReactNode } from 'react'
import { Pressable, Text, View } from 'react-native'
import { Feather, Foundation } from '../../../../components/icon/vectorIcons'
import useTranslation from '../../../../hooks/useTranslation'
import { builderColors, builderRadii } from '../builderColors'

interface FormatButtonProps {
  label: string
  active?: boolean
  disabled?: boolean
  onPress: () => void
  children: ReactNode
}

const FormatButton = ({
  label,
  active = false,
  disabled = false,
  onPress,
  children
}: FormatButtonProps) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={label}
    accessibilityState={{ selected: active, disabled }}
    disabled={disabled}
    onPress={onPress}
    style={{
      width: 28,
      height: 28,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: builderRadii.md,
      backgroundColor: active ? builderColors.activeFill : 'transparent',
      opacity: disabled ? 0.35 : 1
    }}
  >
    {children}
  </Pressable>
)

const Divider = () => (
  <View
    style={{
      width: 1,
      height: 16,
      marginHorizontal: 4,
      backgroundColor: builderColors.border
    }}
  />
)

const iconColor = (active: boolean) =>
  active ? builderColors.tag : builderColors.text

const HeadingLabel = ({ children, active }) => (
  <Text
    style={{
      fontSize: 12,
      fontWeight: '600',
      color: iconColor(active)
    }}
  >
    {children}
  </Text>
)

export interface TextFormatState {
  blockType: string
  bold: boolean
  italic: boolean
  inLink: boolean
  hasRange: boolean
}

interface TextFormatBarProps {
  format: TextFormatState
  onBlockType: (type: string) => void
  onInlineStyle: (style: 'BOLD' | 'ITALIC') => void
  onLink: () => void
}

/**
 * The Text block's formatting controls, shown as a row at the top of the
 * block while it's selected (so nothing covers the page). Paragraph styles
 * are toggles -- H1 heading, H2 sub-heading, quote, small print, lists --
 * and "none on" is body text. One link button adds a link to selected text
 * or removes the link under the cursor.
 */
const TextFormatBar = ({
  format,
  onBlockType,
  onInlineStyle,
  onLink
}: TextFormatBarProps) => {
  const { t } = useTranslation()
  const { blockType } = format

  return (
    // web only: keep focus (and the text selection) in the editor while the
    // buttons are pressed
    <div onMouseDown={event => event.preventDefault()}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingBottom: 6,
          marginBottom: 8,
          borderBottomWidth: 1,
          borderBottomColor: builderColors.border
        }}
      >
        <FormatButton
          label={t('doclets:text:heading')}
          active={blockType === 'heading'}
          onPress={() => onBlockType('heading')}
        >
          <HeadingLabel active={blockType === 'heading'}>H1</HeadingLabel>
        </FormatButton>
        <FormatButton
          label={t('doclets:text:subheading')}
          active={blockType === 'sub-heading'}
          onPress={() => onBlockType('sub-heading')}
        >
          <HeadingLabel active={blockType === 'sub-heading'}>H2</HeadingLabel>
        </FormatButton>
        <FormatButton
          label={t('doclets:text:quote')}
          active={blockType === 'blockquote'}
          onPress={() => onBlockType('blockquote')}
        >
          <Foundation
            name="quote"
            size={14}
            color={iconColor(blockType === 'blockquote')}
          />
        </FormatButton>
        <FormatButton
          label={t('doclets:text:small')}
          active={blockType === 'small'}
          onPress={() => onBlockType('small')}
        >
          {/* A small "Aa": the style's own size, next to the H1 / H2 labels */}
          <Text
            style={{
              fontSize: 10,
              fontWeight: '600',
              color: iconColor(blockType === 'small')
            }}
          >
            Aa
          </Text>
        </FormatButton>
        <Divider />
        <FormatButton
          label={t('doclets:text:bold')}
          active={format.bold}
          onPress={() => onInlineStyle('BOLD')}
        >
          <Feather name="bold" size={15} color={iconColor(format.bold)} />
        </FormatButton>
        <FormatButton
          label={t('doclets:text:italic')}
          active={format.italic}
          onPress={() => onInlineStyle('ITALIC')}
        >
          <Feather name="italic" size={15} color={iconColor(format.italic)} />
        </FormatButton>
        <FormatButton
          label={t(
            format.inLink ? 'doclets:text:removeLink' : 'doclets:text:addLink'
          )}
          active={format.inLink}
          disabled={!format.inLink && !format.hasRange}
          onPress={onLink}
        >
          <Feather name="link" size={15} color={iconColor(format.inLink)} />
        </FormatButton>
        <Divider />
        <FormatButton
          label={t('doclets:text:bulletList')}
          active={blockType === 'unordered-list-item'}
          onPress={() => onBlockType('unordered-list-item')}
        >
          <Feather
            name="list"
            size={16}
            color={iconColor(blockType === 'unordered-list-item')}
          />
        </FormatButton>
        <FormatButton
          label={t('doclets:text:numberedList')}
          active={blockType === 'ordered-list-item'}
          onPress={() => onBlockType('ordered-list-item')}
        >
          <Foundation
            name="list-number"
            size={16}
            color={iconColor(blockType === 'ordered-list-item')}
          />
        </FormatButton>
      </View>
    </div>
  )
}

export default TextFormatBar
